import { packHead, visibleParastichies, type Seed } from "./phyllotaxis";
import type { AppState, ColorMode } from "./state";

export type Palette = {
  ground: string;
  petal: string;
  petalEdge: string;
  overlay: string;
};

export const PALETTE: Palette = {
  ground: "#100c08",
  petal: "#e6b43c",
  petalEdge: "#c48918",
  overlay: "rgba(248, 232, 196, 0.42)",
};

function hashHue(n: number, mod: number): number {
  const u = ((n * 1.6180339887) % 1 + 1) % 1;
  return (u * 360) / Math.max(1, mod);
}

function kernelFill(seed: Seed, maxR: number): string {
  const t = maxR <= 0 ? 0 : seed.r / maxR;
  const h = 28 + t * 18;
  const s = 58 + t * 22;
  const l = 16 + t * 36;
  return `hsl(${h} ${s}% ${l}%)`;
}

function familyFill(seed: Seed, a: number, b: number): string {
  const mod = seed.n % b;
  const h = 18 + hashHue(mod, b) * 0.22 + (mod % a) * (48 / a);
  const s = 62;
  const l = 28 + (mod / b) * 28;
  return `hsl(${h} ${s}% ${l}%)`;
}

function radiusFill(seed: Seed, maxR: number): string {
  const t = maxR <= 0 ? 0 : seed.r / maxR;
  const h = 22 + t * 48;
  return `hsl(${h} 70% ${22 + t * 32}%)`;
}

function fillFor(seed: Seed, maxR: number, mode: ColorMode, a: number, b: number): string {
  if (mode === "family") return familyFill(seed, a, b);
  if (mode === "radius") return radiusFill(seed, maxR);
  return kernelFill(seed, maxR);
}

function drawPetals(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  count: number,
): void {
  const inner = radius * 0.98;
  const outer = radius * 1.22;
  const width = (Math.PI * 2) / count;
  ctx.save();
  for (let i = 0; i < count; i++) {
    const t = i * width - Math.PI / 2;
    const spread = width * 0.38;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(t) * inner * 0.92, cy + Math.sin(t) * inner * 0.92);
    ctx.quadraticCurveTo(
      cx + Math.cos(t - spread) * (inner + (outer - inner) * 0.45),
      cy + Math.sin(t - spread) * (inner + (outer - inner) * 0.45),
      cx + Math.cos(t) * outer,
      cy + Math.sin(t) * outer,
    );
    ctx.quadraticCurveTo(
      cx + Math.cos(t + spread) * (inner + (outer - inner) * 0.45),
      cy + Math.sin(t + spread) * (inner + (outer - inner) * 0.45),
      cx + Math.cos(t) * inner * 0.92,
      cy + Math.sin(t) * inner * 0.92,
    );
    ctx.closePath();
    ctx.fillStyle = i % 2 === 0 ? PALETTE.petal : PALETTE.petalEdge;
    ctx.fill();
  }
  ctx.restore();
}

function drawSeed(
  ctx: CanvasRenderingContext2D,
  seed: Seed,
  cx: number,
  cy: number,
  scale: number,
  radius: number,
  fill: string,
): void {
  const x = cx + seed.x * scale;
  const y = cy + seed.y * scale;
  const rx = radius * 1.55;
  const ry = radius * 0.86;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(seed.theta + Math.PI / 2);
  ctx.beginPath();
  ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = "rgba(12, 8, 4, 0.45)";
  ctx.lineWidth = Math.max(0.4, radius * 0.12);
  ctx.stroke();
  ctx.restore();
}

function drawFamilies(
  ctx: CanvasRenderingContext2D,
  seeds: Seed[],
  step: number,
  cx: number,
  cy: number,
  scale: number,
): void {
  ctx.save();
  ctx.strokeStyle = PALETTE.overlay;
  ctx.lineWidth = 1.25;
  ctx.lineJoin = "round";
  for (let k = 0; k < step; k++) {
    ctx.beginPath();
    let started = false;
    for (let i = k; i < seeds.length; i += step) {
      const s = seeds[i]!;
      const x = cx + s.x * scale;
      const y = cy + s.y * scale;
      if (!started) {
        ctx.moveTo(x, y);
        started = true;
      } else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.restore();
}

export function paintHead(
  ctx: CanvasRenderingContext2D,
  cssW: number,
  cssH: number,
  dpr: number,
  state: AppState,
): void {
  const w = cssW;
  const h = cssH;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = PALETTE.ground;
  ctx.fillRect(0, 0, w, h);

  const cx = w * 0.5;
  const cy = h * 0.5;
  const margin = Math.min(w, h) * (state.petals ? 0.16 : 0.08);
  const field = Math.max(40, Math.min(w, h) / 2 - margin);
  const packing = 1;
  const seeds = packHead(state.seeds, state.degrees, packing);
  const maxR = Math.sqrt(Math.max(1, state.seeds - 1)) * packing;
  const scale = field / maxR;
  const seedR = (0.46 * packing) * scale;
  const { a, b } = visibleParastichies(state.degrees, state.seeds);

  if (state.petals) drawPetals(ctx, cx, cy, field * 1.02, 34);

  ctx.beginPath();
  ctx.arc(cx, cy, field * 1.01, 0, Math.PI * 2);
  ctx.fillStyle = "#1a120c";
  ctx.fill();

  for (const seed of seeds) {
    drawSeed(ctx, seed, cx, cy, scale, seedR, fillFor(seed, maxR, state.color, a, b));
  }

  if (state.overlay) {
    drawFamilies(ctx, seeds, a, cx, cy, scale);
    drawFamilies(ctx, seeds, b, cx, cy, scale);
  }
}

export function exportPng(canvas: HTMLCanvasElement): void {
  const url = canvas.toDataURL("image/png");
  const a = document.createElement("a");
  a.href = url;
  a.download = "golden-sunflower.png";
  a.click();
}
